import { useEffect, useMemo, useState } from "react";
import { Loader2, Store } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { OPS } from "@/constants/testIds";

/**
 * Dialog to pick a branch and START a draft round.
 *
 * The list of branches comes from the small `/api/branches` endpoint that
 * every other tab uses — no new lookup here. We only show active,
 * non-finance-only branches.
 *
 * On success, the parent lifts the wizard up and unmounts this dialog.
 */
export default function StartRoundDialog({ open, onOpenChange, onStarted }) {
    const { t, tn } = useI18n();
    const [branches, setBranches] = useState([]);
    const [branchId, setBranchId] = useState("");
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        let alive = true;
        setLoading(true);
        api.get("/branches")
            .then((r) => {
                if (!alive) return;
                const list = (r.data || []).filter((b) => b.is_active && !b.finance_only);
                setBranches(list);
                if (list.length && !branchId) setBranchId(list[0].id);
            })
            .catch((e) => alive && toast.error(e?.response?.data?.detail || t("error_generic")))
            .finally(() => alive && setLoading(false));
        return () => { alive = false; };
    }, [open, t, branchId]);

    const start = async () => {
        if (!branchId) return;
        setSaving(true);
        try {
            const r = await api.post("/qc-inspection/inspections/start", { branch_id: branchId });
            onStarted && onStarted(r.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("error_generic"));
        } finally {
            setSaving(false);
        }
    };

    const disabled = loading || saving || !branchId;

    // Grouped label for the trigger.
    const currentBranch = useMemo(() => branches.find((b) => b.id === branchId), [branches, branchId]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid={OPS.qcIns.rounds.startDialog} className="max-w-md">
                <DialogHeader>
                    <DialogTitle>{t("qc_ins_new_round")}</DialogTitle>
                    <DialogDescription>{t("qc_ins_new_round_desc")}</DialogDescription>
                </DialogHeader>

                <div className="space-y-3">
                    <Label className="flex items-center gap-1.5">
                        <Store className="h-4 w-4 text-[color:var(--ink-muted)]" />
                        {t("qc_ins_branch")}
                    </Label>
                    <Select value={branchId} onValueChange={setBranchId} disabled={loading || saving}>
                        <SelectTrigger data-testid={OPS.qcIns.rounds.startBranchSelect}>
                            <SelectValue placeholder={loading ? t("loading") : "—"}>
                                {currentBranch ? tn(currentBranch) : null}
                            </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                            {branches.map((b) => (
                                <SelectItem key={b.id} value={b.id}>
                                    {tn(b)}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                <DialogFooter className="gap-2">
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={saving}
                        data-testid={OPS.qcIns.rounds.startCancel}
                    >
                        {t("cancel")}
                    </Button>
                    <Button
                        onClick={start}
                        disabled={disabled}
                        data-testid={OPS.qcIns.rounds.startConfirm}
                        className="bg-[color:var(--brand-orange)] hover:bg-[color:var(--brand-orange-hover)] text-white"
                    >
                        {saving ? <Loader2 className="h-4 w-4 me-2 animate-spin" /> : null}
                        {t("qc_ins_start_round")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
